// Signed out in Eleplan for Chrome's side panel (app.eleplan.com/panel.html). Sign-in
// isn't done here: Google's and Cognito's pages refuse to load in a frame. It happens in
// a tab of its own, and since the panel shares this browser's storage for Eleplan, the
// panel reloads into Ellie's chat as soon as the tab saves the session.
import React, { useEffect, useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from '../hooks/useTheme';

// tokenStore's keys: a sign-in in the other tab writes them.
const SESSION_KEY_PREFIX = '@eleplan/';

export default function PanelSignInScreen() {
  const theme = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const signedIn = (event: StorageEvent) => {
      if (event.key?.startsWith(SESSION_KEY_PREFIX)) window.location.reload();
    };
    window.addEventListener('storage', signedIn);
    return () => window.removeEventListener('storage', signedIn);
  }, []);

  const open = () => {
    if (typeof window !== 'undefined') window.open(`${window.location.origin}/`, '_blank', 'noopener');
  };

  return (
    <View style={styles.root}>
      <Text style={styles.title} accessibilityRole="header">Sign in to talk with Ellie here</Text>
      <Text style={styles.body}>Sign in to Eleplan in this browser. Her chat opens here as soon as you do.</Text>
      <TouchableOpacity style={styles.button} onPress={open} accessibilityRole="link" accessibilityLabel="Open Eleplan to sign in, in a new tab">
        <Text style={styles.buttonLabel}>Open Eleplan</Text>
      </TouchableOpacity>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  return StyleSheet.create({
    root: {
      flex: 1,
      justifyContent: 'center',
      padding: 24,
      backgroundColor: theme.colors.background,
    },
    title: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 8,
    },
    body: {
      fontSize: 15,
      lineHeight: 21,
      color: theme.colors.muted,
      marginBottom: 20,
    },
    button: {
      alignSelf: 'flex-start',
      borderRadius: 999,
      paddingVertical: 10,
      paddingHorizontal: 20,
      backgroundColor: theme.colors.primary,
    },
    buttonLabel: {
      fontSize: 15,
      fontWeight: '600',
      color: '#ffffff',
    },
  });
}
